/** @format */

import {
  configureStore,
  ThunkAction,
  Action,
  Middleware,
  combineReducers,
  MiddlewareAPI,
  isRejected,
  isPending,
} from "@reduxjs/toolkit";
import { api } from "./services/api";
import cookieReducer from "../features/cookie/cookieSlice";
import editableReducer from "../features/editable/editableSlice";
import featureFlagReducer from "../features/featureFlag/featureFlagSlice";
import printableReducer from "../features/printable/printableSlice";
import progressReducer from "../features/lessonProgress/processSlice";
import resourcesReducer from "../features/resources/resourcesSlice";
import sidebarReducer from "../features/sidebar/sidebarSlice";
import themeReducer from "../features/theme/themeSlice";
import authReducer from "../auth/authSlice";
import contentEditorReducer from "../features/contentEditor/contentEditorSlice";
import deleteModalReducer from "../features/deleteModal/deleteModalSlice";
import notificationsReducer, {
  setAddNotifications,
} from "../features/notifications/notificationsSlice";

const storageKey = "BL_CMS_STATE";

const saveTokenToLocalStorageMiddleware: Middleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    const beforeToken = getState().auth.userToken;
    const result = next(action);
    const afterToken = getState().auth.userToken;
    if (beforeToken?.token !== afterToken?.token) {
      const stringyToken = JSON.stringify({ userToken: afterToken });
      try {
        localStorage.setItem(storageKey, stringyToken);
      } catch (e: any) {
        store.dispatch(
          setAddNotifications({
            date: new Date().toISOString(),
            id: "storageIssue",
            title: "Error saving to browser",
            msg:
              "There was an error saving to your browser. You may need to delete some old resources to free up space. " +
              e?.message,
            type: "danger",
          }),
        );
      }
    }
    return result;
  };

// Listen for mutation errors and create toast notification on failure
const mututationErrorMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    if (action.type.includes("api/executeMutation/") && !isPending(action)) {
      const meta = action.meta;

      let method;

      switch (action.meta.baseQueryMeta.request.method) {
        case "DELETE":
          method = "delete";
          break;
        case "POST":
          method = "save";
          break;
      }

      const endpointNameToReadable = meta.arg.endpointName
        .split(/(?<![A-Z])(?=[A-Z])/)
        .join(" ")
        .toLocaleLowerCase();
      if (isRejected(action)) {
        store.dispatch(
          setAddNotifications({
            date: meta?.fulfilledTimeStamp,
            id: meta.requestId,
            title: `Error on ${method}`,
            msg: `Failed to ${endpointNameToReadable}`,
            type: "danger",
          }),
        );
      }

      /* if (isFulfilled(action)) {
      store.dispatch(setAddNotifications({date: meta?.fulfilledTimeStamp, id: meta.requestId, title: `Success on ${method}`, msg:`Completed ${endpointNameToReadable}`, type: "success"}))
    } */
    }

    return next(action);
  };

const savedState = JSON.parse(localStorage.getItem(storageKey) || "{}");

/**
 * @see https://redux.js.org/usage/writing-tests#setting-up-a-reusable-test-render-function
 */

const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  cookie: cookieReducer,
  editable: editableReducer,
  featureFlag: featureFlagReducer,
  notifications: notificationsReducer,
  printable: printableReducer,
  progress: progressReducer,
  resources: resourcesReducer,
  sidebar: sidebarReducer,
  theme: themeReducer,
  auth: authReducer,
  contentEditor: contentEditorReducer,
  deleteModal: deleteModalReducer,
});

export function setupStore(preloadedState?: Partial<RootState>) {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
      }).concat(
        api.middleware,
        saveTokenToLocalStorageMiddleware,
        mututationErrorMiddleware,
      ),
    devTools: true,
    preloadedState: {
      auth: {
        status: "idle",
        userToken: savedState.userToken,
      },
      ...preloadedState,
    },
  });

  return store;
}

export const store = setupStore();
// export const store = configureStore({
//   reducer: rootReducer,
//   middleware: (getDefaultMiddleware) =>
//     getDefaultMiddleware({
//       immutableCheck: false,
//       serializableCheck: false
//     }).concat(api.middleware, saveTokenToLocalStorageMiddleware),
//   devTools: true,
//   preloadedState: {
//     auth: {
//       status: 'idle',
//       userToken: savedState.userToken
//     }
//   }
// });

// export type AppDispatch = typeof store.dispatch;
export type AppDispatch = AppStore["dispatch"];
// export type RootState = ReturnType<typeof store.getState>;
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
